import { useEffect, useState } from 'react';
import { LogOut, Calendar, LayoutDashboard, Moon, Sun, BarChart3, Wrench, Clock, Zap, Inbox, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { countPendingEvents } from '../services/pendingEventsService';

export const Sidebar = () => {
  const { user, userRole, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(null);

  // Contador da aba "Pendentes" (só admin). Recarrega ao navegar e quando a
  // página de pendentes aprova/descarta algo.
  useEffect(() => {
    if (userRole !== 'admin') return undefined;
    let cancelled = false;
    const refresh = () => {
      countPendingEvents()
        .then((count) => { if (!cancelled) setPendingCount(count); })
        .catch(() => {});
    };
    refresh();
    window.addEventListener('pending-events-changed', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('pending-events-changed', refresh);
    };
  }, [userRole, location.pathname]);

  const isActive = (path) => location.pathname === path;

  const handleNavClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-card border-b border-border flex items-center justify-between px-4 z-40 md:hidden">
        <div className="flex items-center gap-2">
          <img
            src="/logo_eventfy.png"
            alt="Joyn"
            className="w-8 h-8 object-contain rounded-xl"
          />
          <span className="text-base font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Joyn
          </span>
        </div>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg hover:bg-accent transition-colors"
          aria-label="Abrir menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar drawer */}
      <aside className={`fixed left-0 top-0 w-72 h-[100dvh] bg-card border-r border-border flex flex-col z-50 transition-transform duration-300 md:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo_eventfy.png"
              alt="Joyn"
              className="w-10 h-10 object-contain rounded-xl shadow-sm"
            />
            <div className="flex flex-col leading-tight">
              <span
                className="text-lg font-bold tracking-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Joyn
              </span>
              <span className="text-xs text-muted-foreground">
                {userRole === 'admin' ? 'Admin Panel' : 'Organizer Panel'}
              </span>
            </div>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-1 rounded-lg hover:bg-accent transition-colors"
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => handleNavClick(userRole === 'admin' ? '/admin' : '/organizer')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive(userRole === 'admin' ? '/admin' : '/organizer')
                ? 'bg-accent text-accent-foreground'
                : 'hover:bg-accent/50'
            }`}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span className="font-medium">Dashboard</span>
          </button>

          {userRole === 'organizer' && (
            <>
              <button
                onClick={() => handleNavClick('/organizer/past-events')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/organizer/past-events')
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                }`}
                data-testid="nav-past-events"
              >
                <Clock className="w-5 h-5" />
                <span className="font-medium">Eventos Encerrados</span>
              </button>

              <button
                onClick={() => handleNavClick('/organizer/analytics')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/organizer/analytics')
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                }`}
                data-testid="nav-analytics"
              >
                <BarChart3 className="w-5 h-5" />
                <span className="font-medium">Analytics</span>
              </button>
            </>
          )}

          {userRole === 'admin' && (
            <>
              <button
                onClick={() => handleNavClick('/admin/organizers')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/admin/organizers')
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                }`}
              >
                <Calendar className="w-5 h-5" />
                <span className="font-medium">Organizadores</span>
              </button>

              <button
                onClick={() => handleNavClick('/admin/import')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/admin/import')
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                }`}
              >
                <Zap className="w-5 h-5" />
                <span className="font-medium">Novos Eventos IA</span>
              </button>

              <button
                onClick={() => handleNavClick('/admin/pending')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/admin/pending')
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                }`}
              >
                <Inbox className="w-5 h-5" />
                <span className="font-medium">Pendentes</span>
                {pendingCount > 0 && (
                  <span className="ml-auto text-xs font-bold bg-primary text-primary-foreground rounded-full min-w-[1.5rem] px-2 py-0.5 text-center">
                    {pendingCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => handleNavClick('/admin/tools')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/admin/tools')
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                }`}
                data-testid="nav-admin-tools"
              >
                <Wrench className="w-5 h-5" />
                <span className="font-medium">Tools</span>
              </button>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-border space-y-3">
          <div className="px-4 py-2">
            <p className="text-sm font-medium truncate">{user?.name || user?.email}</p>
            <p className="text-xs text-muted-foreground capitalize">{userRole}</p>
          </div>

          <Button
            variant="ghost"
            className="w-full justify-start gap-3"
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
          >
            {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Sair</span>
          </Button>
        </div>
      </aside>
    </>
  );
};
